// CustomImage.ts
import { Plugin } from 'prosemirror-state';
import Image from '@tiptap/extension-image';

const IMAGE_SIZE_STYLE: Record<'small' | 'medium' | 'large', string> = {
  small: 'max-width:150px;height:auto;',
  medium: 'max-width:300px;height:auto;',
  large: 'max-width:600px;height:auto;',
};

export const CustomImage = (
  uploadToGCS: (file: File) => Promise<{ url: string; mimeType: string; publicUrl: string }>,
  onError?: (message: string) => void // ✅ Error handler
) =>
  Image.extend({
    addAttributes() {
      return {
        src: {
          default: null,
          parseHTML: (el) => el.getAttribute('src'),
          renderHTML: ({ src }) => ({ src }),
        },
        size: {
          default: 'medium',
          parseHTML: (el) => el.getAttribute('data-size') || 'medium',
          renderHTML: ({ size }) => ({
            'data-size': size,
            style: IMAGE_SIZE_STYLE[size as 'small' | 'medium' | 'large'],
          }),
        },
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handlePaste: (view, event) => {
              const items = Array.from(event.clipboardData?.items || []);
              const imageItem = items.find((item) => item.type.includes('image'));

              if (!imageItem) return false;

              const file = imageItem.getAsFile();
              if (!file) return false;

              // ✅ Limit file size
              const MAX_SIZE_MB = 1;
              if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                onError?.('Pasted image must be under 1MB.');
                return true;
              }

              setTimeout(async () => {
                try {
                  const { url, mimeType, publicUrl } = await uploadToGCS(file);

                  await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': mimeType },
                    body: file,
                  });

                  if (publicUrl) {
                    onError?.('');
                    view.dispatch(
                      view.state.tr.replaceSelectionWith(
                        this.type.create({
                          src: publicUrl,
                          size: 'medium',
                        })
                      )
                    );
                  }
                } catch (err) {
                  console.error('Paste image upload failed:', err);
                  onError?.('Failed to upload image. Please try again.');
                }
              }, 0);

              return true;
            },
          },
        }),
      ];
    },
  });

import { common, createLowlight } from 'lowlight';
import LinkExtension from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { mergeClasses } from 'minimal-shared/utils';
import StarterKitExtension from '@tiptap/starter-kit';
import TextAlignExtension from '@tiptap/extension-text-align';
import PlaceholderExtension from '@tiptap/extension-placeholder';
import CodeBlockLowlightExtension from '@tiptap/extension-code-block-lowlight';
import { useEditor, EditorContent, ReactNodeViewRenderer } from '@tiptap/react';
// Editor.tsx
import { useRef, useState, useEffect, forwardRef, useCallback, useImperativeHandle } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Portal from '@mui/material/Portal';
import Backdrop from '@mui/material/Backdrop';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormHelperText from '@mui/material/FormHelperText';

import { useTranslate } from 'src/locales';
import { usePutPresignedMediaUrlMutation } from 'src/pages/chat/slice/chatApi';

import { Toolbar } from './toolbar';
import { EditorRoot } from './styles';
import { editorClasses } from './classes';
import { CodeHighlightBlock } from './components/code-highlight-block';

import type { EditorProps } from './types';

export const Editor = forwardRef<any, EditorProps>((props, ref) => {
  const {
    sx,
    error,
    onChange,
    slotProps,
    helperText,
    resetValue,
    className,
    editable = true,
    fullItem = false,
    value: content = '',
    placeholder = 'Write something awesome...',
    direction = 'rtl',
    isEmailEditor = false,
    isSignatureEditor = false,
    ...other
  } = props;
  const { t } = useTranslate('translation');
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [putPresignedMediaUrl] = usePutPresignedMediaUrlMutation();
  const [fullScreen, setFullScreen] = useState(false);
  const handleToggleFullScreen = useCallback(() => setFullScreen((prev) => !prev), []);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadToGCS = async (file: File) => {
    const { url, mimeType, publicUrl } = await putPresignedMediaUrl({
      fileName: file.name,
      fileType: file.type,
      isPublic: true,
    }).unwrap();
    return { url, mimeType, publicUrl };
  };

  const lowlight = createLowlight(common);

  const editor = useEditor({
    content,
    editable,
    extensions: [
      Underline,
      StarterKitExtension.configure({
        codeBlock: false,
        code: { HTMLAttributes: { class: editorClasses.content.codeInline } },
        heading: { HTMLAttributes: { class: editorClasses.content.heading } },
        horizontalRule: { HTMLAttributes: { class: editorClasses.content.hr } },
        listItem: { HTMLAttributes: { class: editorClasses.content.listItem } },
        blockquote: { HTMLAttributes: { class: editorClasses.content.blockquote } },
        bulletList: { HTMLAttributes: { class: editorClasses.content.bulletList } },
        orderedList: { HTMLAttributes: { class: editorClasses.content.orderedList } },
      }),
      PlaceholderExtension.configure({
        placeholder,
        emptyEditorClass: editorClasses.content.placeholder,
      }),
      CustomImage(uploadToGCS, setUploadError),
      TextAlignExtension.configure({ types: ['heading', 'paragraph'] }),
      LinkExtension.configure({
        autolink: true,
        openOnClick: false,
        HTMLAttributes: { class: editorClasses.content.link },
      }),
      CodeBlockLowlightExtension.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeHighlightBlock);
        },
      }).configure({ lowlight, HTMLAttributes: { class: editorClasses.content.codeBlock } }),
    ],
    onUpdate({ editor: _editor }) {
      const html = _editor.getHTML();
      const text = _editor.getText();
      const textAlignments = ['left', 'center', 'justify', 'right'];

      const isFiltered =
        _editor.isActive('bold') ||
        _editor.isActive('italic') ||
        _editor.isActive('underline') ||
        _editor.isActive('bulletList') ||
        _editor.isActive('orderedList') ||
        textAlignments.some((align) => _editor.isActive({ textAlign: align })) ||
        Array.from({ length: 6 }, (_, i) => i + 1).some((level) =>
          _editor.isActive('heading', { level })
        );

      onChange?.(isEmailEditor ? html : isFiltered ? html : text);
    },
    ...other,
  });

  // 🔑 Expose API to parent (and replace focus handling)
  useImperativeHandle(ref, () => ({
    insertSignature: (html: string) => {
      editor?.commands.insertContent(html);
    },
    setEditorContent: (html: string) => {
      editor?.commands.setContent(html);
    },
    focus: () => {
      editor?.commands.focus();
    },
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      if (editor?.isEmpty && content !== '<p></p>') {
        editor.commands.setContent(content);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [content, editor]);

  useEffect(() => {
    if (resetValue && !content) {
      editor?.commands.clearContent();
    }
  }, [content]);

  useEffect(() => {
    document.body.style.overflow = fullScreen ? 'hidden' : '';
  }, [fullScreen]);

  const hasError = !!(uploadError || error);
  const displayMessage = uploadError || helperText;

  useEffect(() => {
    if (uploadError && editor?.getText()) {
      setUploadError(null);
    }
  }, [editor?.getText()]);

  return (
    <Portal disablePortal={!fullScreen}>
      {fullScreen && <Backdrop open sx={(theme) => ({ zIndex: theme.zIndex.modal - 1 })} />}

      <Box
        {...slotProps?.wrapper}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...(!editable && { cursor: 'not-allowed' }),
          ...slotProps?.wrapper?.sx,
        }}
      >
        <EditorRoot
          error={hasError}
          disabled={!editable}
          fullScreen={fullScreen}
          className={mergeClasses([editorClasses.root, className])}
          sx={{
            border: '1px solid',
            borderColor: hasError ? 'error.main' : 'divider',
            borderRadius: 2,
            ...(sx || {}),
          }}
          dir={direction}
        >
          <Toolbar
            editor={editor}
            fullItem={fullItem}
            fullScreen={fullScreen}
            onToggleFullScreen={handleToggleFullScreen}
            isSignatureEditor={isSignatureEditor}
          />

          {editor && isSignatureEditor && editor.getAttributes('image')?.src && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ButtonGroup size="small" sx={{ my: 1, mx: 2 }}>
                <Button
                  onClick={() =>
                    editor.chain().focus().updateAttributes('image', { size: 'small' }).run()
                  }
                >
                  {t('SMALL')}
                </Button>
                <Button
                  onClick={() =>
                    editor.chain().focus().updateAttributes('image', { size: 'medium' }).run()
                  }
                >
                  {t('MEDIUM')}
                </Button>
                <Button
                  onClick={() =>
                    editor.chain().focus().updateAttributes('image', { size: 'large' }).run()
                  }
                >
                  {t('LARGE')}
                </Button>
              </ButtonGroup>
            </Box>
          )}

          <EditorContent
            ref={contentRef}
            spellCheck="false"
            autoComplete="off"
            autoCapitalize="off"
            editor={editor}
            className={editorClasses.content.root}
            dir={direction}
          />
        </EditorRoot>

        {displayMessage && (
          <FormHelperText
            error={hasError}
            sx={{
              px: 2,
            }}
          >
            {displayMessage}
          </FormHelperText>
        )}
      </Box>
    </Portal>
  );
});
